import { WorkflowDiagnosticSubject } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { WorkflowDiagnosticFinding, WorkflowRepairReport } from '@/domain/workflow-admin.types'
import { useRepairWorkOrders, useWorkflowDiagnostics } from '@/hooks/useWorkflowAdmin'
import { DiagnosticsTable } from './DiagnosticsTable'
import { RepairResults } from './RepairResults'
import { EmptyState } from './states'

/**
 * The diagnostics report and the repair flow it feeds. Repair is always
 * preview-then-apply: the preview is a dry run that writes nothing, and
 * applying calls the backend a second time rather than replaying the preview,
 * so the results shown afterwards are what actually happened.
 */

/** Work Orders named by findings that repair can act on. */
const repairTargets = (findings: WorkflowDiagnosticFinding[]): string[] => {
	const ids = findings
		.filter((finding) => finding.subject === WorkflowDiagnosticSubject.WORK_ORDER)
		.map((finding) => finding.context.workOrderId ?? finding.resourceId)
		.filter((id): id is string => typeof id === 'string')
	return Array.from(new Set(ids))
}

function RepairPanel({ targets }: { targets: string[] }) {
	const repair = useRepairWorkOrders()
	const [preview, setPreview] = useState<WorkflowRepairReport | null>(null)
	// The preview belongs to the exact selection it was produced from; once the
	// selection moves, applying it would act on something the administrator
	// never saw, so the stale preview stops authorizing an apply.
	const [previewedFor, setPreviewedFor] = useState<string>('')
	const selectionKey = targets.join(',')
	const stale = preview !== null && previewedFor !== selectionKey
	const applied = repair.data && !repair.data.dryRun ? repair.data : null

	if (targets.length === 0) {
		return <EmptyState title="Nothing to repair" description="No Work Order findings are eligible for repair." />
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-sm text-ds-text-muted">{targets.length} Work Order(s) selected</span>
				<Button
					isDisabled={repair.isPending}
					onPress={() =>
						repair.mutate(
							{ workOrderIds: targets, dryRun: true },
							{
								onSuccess: (report) => {
									setPreview(report)
									setPreviewedFor(selectionKey)
								},
							},
						)
					}
				>
					{repair.isPending ? 'Working…' : 'Preview repair'}
				</Button>
				<Button
					isDisabled={repair.isPending || preview === null || stale}
					onPress={() => repair.mutate({ workOrderIds: targets, dryRun: false })}
				>
					Apply repair
				</Button>
				{stale && <span className="text-sm text-ds-warning">Selection changed — preview again before applying.</span>}
			</div>
			{/* Once applied, the applied report replaces the preview entirely. */}
			<RepairResults
				report={applied ?? preview ?? undefined}
				isLoading={repair.isPending}
				error={repair.isError ? repair.error : undefined}
			/>
		</div>
	)
}

export function WorkflowDiagnosticsPage() {
	const [page, setPage] = useState(1)
	const diagnostics = useWorkflowDiagnostics(page)
	const findings = diagnostics.data?.data ?? []

	return (
		<div className="space-y-6">
			<div>
				<Link className="text-sm text-ds-primary hover:underline" to="/admin/workflows">
					← All workflows
				</Link>
				<h1 className="text-2xl font-semibold">Workflow diagnostics</h1>
				<p className="text-ds-text-muted">
					Configuration problems for this tenant, and the repairs available for them.
				</p>
			</div>
			<SectionCard title="Findings">
				<DiagnosticsTable
					report={diagnostics.data}
					isLoading={diagnostics.isLoading}
					error={diagnostics.isError ? diagnostics.error : undefined}
					onRetry={() => void diagnostics.refetch()}
					onPageChange={setPage}
				/>
			</SectionCard>
			{!diagnostics.isLoading && !diagnostics.isError && (
				<SectionCard
					title="Repair Work Order associations"
					description="Preview first. Applying re-resolves each Work Order's workflow and never changes its status."
				>
					<RepairPanel targets={repairTargets(findings)} />
				</SectionCard>
			)}
		</div>
	)
}
